<template>
  <div class="ai-visibility-form-step">
    <step-intro
      title="Where should we check AI results?"
      description="Market affects results - especially for Google AI answers"
    />

    <div class="ai-visibility-form-step__field">
      <span>Market*</span>
      <v-autocomplete
        class="ai-visibility-form-step__autocomplete"
        :items="locations"
        item-text="location_name"
        item-value="location_code"
        :value="Number(locationCode)"
        append-icon="keyboard_arrow_down"
        color="mainColor"
        dense
        outlined
        hide-details="auto"
        persistent-hint
        @change="selectLocation"
      />
      <span class="helper">Some platforms vary by location more than others.</span>
    </div>

    <div class="ai-visibility-form-step__field mt-4">
      <span>Language</span>
      <v-autocomplete
        class="ai-visibility-form-step__autocomplete"
        :items="languages"
        item-text="language_name"
        item-value="language_code"
        :value="languageCode"
        append-icon="keyboard_arrow_down"
        color="mainColor"
        dense
        outlined
        hide-details="auto"
        @change="selectLanguage"
      />
    </div>
  </div>
</template>

<script>
import locationsData from '@/consts/dataforseo_locations.json'
import languagesData from '@/consts/dataforseo_languages.json'
import StepIntro from '../ui/StepIntro.vue'
import { EXTRA_LANGUAGES } from '../../constants'

/**
 * The shared DataForSEO list with the tracker's own regional variants inserted after their base
 * language. The list's `isDivider` flag only marks the end of its priority block (English ... Portuguese);
 * filtering on it used to drop Portuguese from this step.
 */
function buildLanguages () {
  const languages = (languagesData.languages || languagesData)
    .map(({ language_name, language_code }) => ({ language_name, language_code }))
  EXTRA_LANGUAGES.forEach(({ language_name, language_code, after }) => {
    const index = languages.findIndex((language) => language.language_code === after)
    languages.splice(index >= 0 ? index + 1 : languages.length, 0, { language_name, language_code })
  })
  return languages
}

export default {
  name: 'MarketStep',
  components: {
    StepIntro,
  },
  props: {
    market: {
      type: String,
      default: 'United States',
    },
    locationCode: {
      type: [Number, String],
      default: 2840,
    },
    countryIsoCode: {
      type: String,
      default: 'US',
    },
    language: {
      type: String,
      default: 'English',
    },
    languageCode: {
      type: String,
      default: 'en',
    },
  },
  data () {
    return {
      locations: (locationsData.locations || locationsData).filter((location) => location.location_type === 'Country'),
      languages: buildLanguages(),
    }
  },
  methods: {
    selectLocation (value) {
      const locationCode = Number(value)
      const location = this.locations.find((item) => item.location_code === locationCode)
      if (!location) {
        return
      }

      this.$emit('update-location', {
        market: location.location_name,
        locationCode: location.location_code,
        countryIsoCode: location.country_iso_code,
      })
    },
    selectLanguage (languageCode) {
      const language = this.languages.find((item) => item.language_code === languageCode)
      if (!language) {
        return
      }

      this.$emit('update-language-option', {
        language: language.language_name,
        languageCode: language.language_code,
      })
    },
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-form-step__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;

  span {
    color: var(--v-mainColor-base);
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
  }

  .helper {
    min-height: 15px;
    color: var(--v-gray2Color-base);
    font-size: 12px;
    letter-spacing: 0.12px;
    line-height: normal;
  }

  .ai-visibility-form-step__autocomplete {
    width: 100%;

    ::v-deep fieldset {
      border-color: #DADADA;
      background-color: var(--v-cardBg-base);
    }

    ::v-deep input {
      color: var(--v-mainColor-base);
      outline-color: #DADADA;
    }

    ::v-deep .v-messages__message {
      color: var(--v-red11Color-base);
      font-size: 12px;
      line-height: 1.4;
    }
  }
}
</style>
